import {
  createEffect,
  createMemo,
  createResource,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { PowerNow, SiteNode } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber } from "../format.ts";
import { scope, setScope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { freshness } from "../overview.ts";

/** Power now is fetched for at most this many places; the rest are listed without it. */
const MAX_WITH_POWER = 12;

interface Site {
  node: SiteNode;
  points: number;
  now: PowerNow | null;
}

/**
 * The organisation's places on a map (prototype "Obiekty organizacji"): every node with a position, coloured by
 * whether its data is current and sized by power now. Choosing one makes it the scope of the overview.
 */
export function SitesMap() {
  const placed = createMemo(() =>
    (siteTree()?.nodes ?? []).filter(
      (n) => n.latitude !== null && n.longitude !== null,
    ),
  );
  return (
    <Show when={placed().length > 0}>
      <SitesCard placed={placed()} />
    </Show>
  );
}

// Leaflet sets SVG attributes, where CSS variables do not apply: the theme's colours are read once.
const color = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  "#888";

function SitesCard(props: { placed: SiteNode[] }) {
  const navigate = useNavigate();
  const placed = () => props.placed;
  const [sites] = createResource(
    () => (placed().length > 0 ? placed() : undefined),
    async (nodes) => {
      const tree = siteTree()!;
      return Promise.all(
        nodes.map(async (node, i): Promise<Site> => {
          const inside = new Set(subtreeOf(tree.nodes, node.id));
          const now =
            i < MAX_WITH_POWER
              ? await api.sites.now(node.id).catch(() => null)
              : null;
          return {
            node,
            points: tree.points.filter((p) => inside.has(p.nodeId)).length,
            now,
          };
        }),
      );
    },
  );
  const state = (s: Site) => {
    const times = (s.now?.points ?? []).map((p) => freshness(p.at, Date.now()));
    return times.length === 0
      ? "none"
      : times.every((x) => x === "ok")
        ? "ok"
        : times.some((x) => x === "ok")
          ? "late"
          : "none";
  };
  const choose = (id: string) => {
    setScope(id);
    navigate("/overview");
  };

  let host!: HTMLDivElement;
  let map: L.Map | undefined;
  let layer: L.LayerGroup | undefined;
  onMount(() => {
    map = L.map(host, { scrollWheelZoom: false, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      className: "street-tiles",
      maxZoom: 18,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    layer = L.layerGroup().addTo(map);
    onCleanup(() => map?.remove());
  });
  createEffect(() => {
    const all = sites();
    if (!map || !layer || !all || all.length === 0) {
      return;
    }
    layer.clearLayers();
    const bounds: L.LatLngTuple[] = [];
    for (const s of all) {
      const at: L.LatLngTuple = [s.node.latitude!, s.node.longitude!];
      bounds.push(at);
      const st = state(s);
      const kw = Math.abs(s.now?.consumption ?? 0);
      L.circleMarker(at, {
        radius: Math.max(7, Math.min(16, Math.sqrt(kw || 4) * 3)),
        color: "#fff",
        weight: 2,
        fillColor: color(
          st === "ok" ? "--ok" : st === "late" ? "--warn" : "--crit",
        ),
        fillOpacity: 0.9,
      })
        .bindTooltip(s.node.name)
        .on("click", () => choose(s.node.id))
        .addTo(layer);
    }
    if (bounds.length === 1) {
      map.setView(bounds[0]!, 17);
    } else {
      // As close as the map goes while every place stays in view.
      map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 18 });
    }
  });

  return (
    <section class="card">
      <div class="card-h">
        <h2>{t("overview.sites.title")}</h2>
        <span class="meta">
          {t("overview.sites.meta", { count: placed().length })}
        </span>
      </div>
      <div class="mapgrid">
        <div style={{ padding: "10px" }}>
          <div
            ref={host}
            class="map"
            style={{ height: "320px" }}
            role="region"
            aria-label={t("overview.sites.title")}
          />
        </div>
        <div class="side">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("overview.sites.site")}</th>
                <th class="r">{t("overview.sites.now")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={sites() ?? []}>
                {(s) => (
                  <tr
                    class={`click${scope()?.id === s.node.id ? " sel" : ""}`}
                    onClick={() => choose(s.node.id)}
                  >
                    <td>
                      <b style={{ "font-weight": 500 }}>{s.node.name}</b>
                      <div class="hint">
                        {t("overview.pointCount", { count: s.points })}
                      </div>
                      <Show when={state(s) !== "ok" && s.points > 0}>
                        <span
                          class={`chip ${state(s) === "late" ? "warn" : "crit"}`}
                        >
                          {t(
                            state(s) === "late"
                              ? "overview.sites.late"
                              : "overview.noReading",
                          )}
                        </span>
                      </Show>
                    </td>
                    <td class="r num">
                      {s.now?.hasBoundary
                        ? `${formatNumber(s.now.consumption, locale(), 2)} kW`
                        : "—"}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
